import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Select, Table } from 'antd';
import { SelectProps } from 'antd/lib';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { FormSubsection } from '@/components/general/FormSection';
import InfoTooltip from '@/components/general/InfoTooltip';
import { FailReportWarrantyString } from '@/model/FailReport/FailReport';

import useTicketFormFailInfo, { TicketFailItem } from './useTicketFormFailInfo';

interface TicketFormFailInfoProps {
  failCategorySelectProps: SelectProps;
  initialItems?: TicketFailItem[];
}

const TicketFormFailInfo = (props: TicketFormFailInfoProps) => {
  const { t } = useTranslation();
  const { failCategorySelectProps, initialItems } = props;
  const {
    items,
    newProductType,
    newProduct,
    newSerialNumber,
    newComments,
    setNewComments,
    needsSerialNumber,
    showSerialColumn,
    productsSelectProps,
    serialNumbersSelectProps,
    productTypeOptions,
    handleAdd,
    handleDelete,
    handleProductTypeChange,
    handleProductChange,
    handleSerialNumberChange,
  } = useTicketFormFailInfo(initialItems);

  return (
    <FormSubsection
      title={
        <>
          {t('Detalle del problema')}{' '}
          <InfoTooltip
            tooltipText={`${t(
              'Información adicional para el analisis del problema asociado al ticket',
            )}. ${t('Su utilidad es la generación de metricas futuras para mejoras de productos')}`}
          />
        </>
      }
      style={{ margin: '24px 0 12px' }}>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Categoría del problema')}
          name={'fail_report_category'}
          rules={[
            { required: true, message: t('La {{field}} es requerida', { field: t('categoría') }) },
          ]}>
          <AntdSelect
            {...failCategorySelectProps}
            placeholder={t('Seleccione una categoría')}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="En garantía"
          name="fail_report_warranty"
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('Estado de garantía') }),
            },
          ]}>
          <AntdSelect
            options={Object.entries(FailReportWarrantyString).map(([value, label]) => ({
              value: Number(value),
              label: t(label),
            }))}
          />
        </Form.Item>
      </div>

      <Form.Item label={t('Comentario')} name={'fail_report_comment'}>
        <Input.TextArea />
      </Form.Item>

      {/* Tabla inline de elementos involucrados en la falla */}
      <div style={{ marginTop: 16 }}>
        <Table<TicketFailItem>
          size="small"
          dataSource={items}
          rowKey="key"
          pagination={false}
          locale={{ emptyText: t('Sin elementos cargados') }}
          footer={() => (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select
                size="small"
                placeholder={t('Tipo')}
                value={newProductType}
                onChange={handleProductTypeChange}
                options={productTypeOptions}
                allowClear
                style={{ flex: '0 0 120px' }}
              />
              <Select
                size="small"
                placeholder={t('Seleccione elemento')}
                value={newProduct}
                onChange={handleProductChange}
                {...productsSelectProps}
                disabled={newProductType === undefined}
                showSearch
                allowClear
                style={{ flex: '1 1 180px', minWidth: 150 }}
              />
              {needsSerialNumber && (
                <Select
                  size="small"
                  placeholder={t('Numero de serie')}
                  value={newSerialNumber}
                  onChange={handleSerialNumberChange}
                  {...serialNumbersSelectProps}
                  showSearch
                  allowClear
                  style={{ flex: '0 1 140px', minWidth: 120 }}
                />
              )}
              <Input
                size="small"
                placeholder={t('Comentario')}
                value={newComments}
                onChange={e => setNewComments(e.target.value)}
                style={{ flex: '1 1 150px', minWidth: 120 }}
                onPressEnter={handleAdd}
              />
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                disabled={newProduct === undefined}>
                {t('Agregar')}
              </Button>
            </div>
          )}>
          <Table.Column<TicketFailItem> title={t('Elemento')} dataIndex="name" />
          {showSerialColumn && (
            <Table.Column<TicketFailItem> title={t('Numero de serie')} dataIndex="serial_number" />
          )}
          <Table.Column<TicketFailItem> title={t('Comentario')} dataIndex="comment" />
          <Table.Column<TicketFailItem>
            width={50}
            render={(_, record) => (
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.key)}
              />
            )}
          />
        </Table>
      </div>

      {/* Campo oculto: ítems mapeados al shape de fail_report_data_update.fail_report_items */}
      <Form.Item name="fail_report_items" hidden>
        <Input />
      </Form.Item>
    </FormSubsection>
  );
};

export default TicketFormFailInfo;
